import {action, computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import {default as handleErrorToast} from "../../../components/toast/ErrorToast";
import {default as handleSuccessToast} from "../../../components/toast/SuccessToast";
import RoleAdapter from "../../../adapters/RoleAdapter";
import FormSet from "../../../forms/fields/FormSet";
import UserForm from "../../../forms/fields/UserForm";
import CustomerOnboardingPanelFeature from "./CustomerOnboardingPanelFeature";

export default class CustomerOnboardingNewUsersFeature extends CustomerOnboardingPanelFeature {
    static MARKETPLACE_ADMINISTRATOR_ROLE_NAME = 'FRONTOFFICE_GENERAL_ADMINISTRATOR_ROLE';
    static DUPLICATED_USER_ONBOARDING_REQUEST_EMAIL_ERROR_CODE = 'duplicated_user_onboarding_request_email';

    constructor(application, generalFeature) {
        super(application, generalFeature);
        this._rolesOptions = [];
        this._usersFormSet = null;

        makeObservable(this, {
            _rolesOptions: observable,
            rolesOptions: computed,
            _usersFormSet: observable,
            usersFormSet: computed,
            addUser: action,
            removeUser: action,
        })
    }

    async load() {
        await this._allRoles()
        this._createUsersFormSet()
        this.finishLoading()
    }

    _createUsersFormSet() {
        const userOnboardingRequests = this.customerOnboarding().request.userOnboardingRequests;
        this._usersFormSet = new FormSet([], this);
        userOnboardingRequests.forEach(userOnboardingRequest => this.addUserOnboardingRequestFor(userOnboardingRequest));
    }

    addUserOnboardingRequestFor(userOnboardingRequest) {
        const userOnboardingRequestForm = UserForm.newFor(userOnboardingRequest, this, this.customerOnboarding().configuration);
        this._usersFormSet.addForm(userOnboardingRequestForm);
        return userOnboardingRequestForm;
    }

    addUser() {
        const userOnboardingRequestForm = UserForm.newEmpty(this, this.customerOnboarding().configuration);
        this._usersFormSet.addForm(userOnboardingRequestForm);
        return userOnboardingRequestForm;
    }

    removeUser(userId) {
        this._usersFormSet.removeForm(userId);
    }

    createForm(){
        return this._createUsersFormSet()
    }

    get usersFormSet() {
        return this._usersFormSet;
    }

    get form() {
        return this.usersFormSet
    }

    rolesFieldOptions(){
        return this.rolesOptions.map(value => {
            return {label: new RoleAdapter(value).adapt(), value: value}
        });
    }

    deliverySitesFieldOptions() {
        return this.deliverySitesOptions().map(deliverySite => {
            return {
                label: `${deliverySite.name} - ${deliverySite.address} - ${deliverySite.city}`,
                value: deliverySite.id
            }
        });
    }

    _deliverySiteFieldKeyValueFor(deliverySite) {
        return {
            code: deliverySite.id,
            name: deliverySite.name,
        };
    }

    async _allRoles() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._rolesOptions = response.roles()
            },
            handlesError: (response) => {
                handleErrorToast('No se pudieron obtener los roles')
            }
        });
        return this._application.apiClient().allRoles(responseHandler)
    }

    async deleteEntity(userOnboardingRequest) {
        if (userOnboardingRequest.id) {
            const responseHandler = new ApiResponseHandler({
                handlesSuccess: async (response) => {
                    await this.customerOnboardingHasChanged()
                    // this._createUsersFormSet()
                    handleSuccessToast("Usuario eliminado correctamente.");
                },
                handlesError: (response) => {
                    handleErrorToast("No se pudo borrar el usuario.");
                }
            });
            return this._application.apiClient().deleteUserOnboardingRequest(userOnboardingRequest.id, responseHandler);
        }
    }

    async formChanged(userOnboardingRequest) {
        await this.editOrCreateUserOnboardingRequest(userOnboardingRequest);
    }

    async editOrCreateUserOnboardingRequest(userOnboardingRequest) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                await this.customerOnboardingHasChanged()
                this._createUsersFormSet();
                handleSuccessToast("Datos guardados correctamente.");
            },
            handlesError: (response) => {
                this._showEditOrCreateUserOnboardingRequestErrorToast(response);
            }
        });
        return this._application.apiClient().editOrCreateUserOnboardingRequests(this.customerOnboarding().request.taxpayerIdentificationNumber,
            userOnboardingRequest, responseHandler);
    }

    _showEditOrCreateUserOnboardingRequestErrorToast(response) {
        const hasADuplicatedEmailInThisOnboarding = response.errors().some(error =>
            error.code === CustomerOnboardingNewUsersFeature.DUPLICATED_USER_ONBOARDING_REQUEST_EMAIL_ERROR_CODE);
        if (hasADuplicatedEmailInThisOnboarding) {
            handleErrorToast("Ese email ya está cargado en esta alta.");
        } else {
            handleErrorToast("No se pudieron guardar los datos.");
        }
    }

    get rolesOptions(){
        return this._rolesOptions
    }

    deliverySitesOptions(){
        return this.customerOnboarding().request.deliverySitesOnboardingRequests
    }

    canEditUserOnboardingRequests() {
        return !(this.customerOnboarding().configuration.readonlyFields.includes('_user_onboarding_requests'));
    }

    hasAUserWithMarketplaceAdministratorRole() {
        const userOnboardingRequests = this.customerOnboarding().request.userOnboardingRequests;
        return userOnboardingRequests.some(userOnboardingRequest =>
            userOnboardingRequest.roles.includes(CustomerOnboardingNewUsersFeature.MARKETPLACE_ADMINISTRATOR_ROLE_NAME));
    }

    isComplete() {
        return super.isComplete() && this.hasAUserWithMarketplaceAdministratorRole();
    }

}